// Terminal detail modal -- openTerminalImpl, closeModal, switchModalTab,
// saveSettings, editTerminalField, showConfirm, setPendingSetting,
// toggleStatus, hopper refresh, prepareTerminal, renderCurrencyTotals,
// hasOwn, renderTab dispatcher + per-tab renderers (renderTabOverview,
// renderTabLogs, renderTabSettings, renderTabCommands, renderTabAccounting).
//
// Ported from admin/index.php inline <script> block (L1190-L2442).
// Functions take state: TimestopState + config where needed.
// Module-scope lets (currentTerminal, terminals, hopperRefreshTimer,
// modalChartInstance) are accessed via state.* in the bundle version.
//
// Tab rendering model (Phase 10):
//   - paintTerminalModal opens the modal backdrop and fills the chrome
//     (name, location, status, icon, hwid) plus paints the Overview tab.
//   - Other tabs (1=Logs, 2=Settings, 3=Commands, 4=Accounting) are
//     painted LAZILY on first activation by switchModalTab(n).
//   - Public renderTab(n) re-paints tab n from current state -- useful
//     after a save action that should refresh Commands or Accounting.
//   - Each per-tab render is a top-level exported function so it can be
//     called individually (window.renderTabOverview(2) etc.) if needed.

import {
  buildCurrencyLookup,
  displayTerminalName,
  displayTerminalIcon,
  escapeHtml,
  formatCurrencyByISO,
  formatCurrency,
  formatTs,
  getTotalByCurrency,
  lastSeenFor,
  logKey,
} from './utils';
import { isOperator } from './auth';
import type { ApiFetchConfig, ApiResponse } from './api';
import { apiFetch } from './api';
import type { TimestopState } from './state';
import type { Terminal } from './types';
import {formatDividedCurrency, formatDividedCurrencyByISO} from "./utils/currency";
import {fetchData} from "./data";
import { showSpinner, hideSpinner } from './spinner';
// Imported under the alias `i18n` (rather than `t`) because the
// legacy renderTabCommands / renderTabOverview / renderTabSettings /
// etc. use `t` as a Terminal loop variable. Without the alias, every
// `i18n('…')` call in those renderers would resolve to the Terminal
// parameter and the typecheck would trip with "Terminal has no call
// signatures". render.ts uses the same pattern.
import { t as i18n } from './i18n';

// Helper to safely access window properties without TS complaining about
// the missing index signature on the Window type.
function win(): Record<string, unknown> {
  return window as unknown as Record<string, unknown>;
}

// Lightweight ApiFetchFn -- same pattern as auth.ts
type ApiFetchFn = (
  action: string,
  opts: Record<string, unknown>,
  config: ApiFetchConfig,
) => Promise<ApiResponse>;

// Resolve the live app state. The bundle's window.timestop.state is the
// single source of truth; this helper just hides the cast.
function liveState(): TimestopState {
  return (window as unknown as { timestop: { state: TimestopState } }).timestop.state;
}

// hasOwn -- Object.prototype.hasOwnProperty.call wrapper
export function hasOwn(obj: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

// Build currency lookup table on the terminal if not present, extract
// hardware version from hw_info. Mutates terminal in place.
export function prepareTerminal(terminal: Terminal): Terminal {
  if (terminal.cur_recv) {
    (terminal as Record<string, unknown>).currencyLookup = buildCurrencyLookup(terminal.cur_recv);
  }
  (terminal as Record<string, unknown>).version = '_';
  const hw = (terminal as Record<string, unknown>).hw_info;
  if (hw && typeof hw === 'object' && 'version' in hw) {
    (terminal as Record<string, unknown>).version = (hw as Record<string, unknown>).version;
  }
  return terminal;
}

// Render currency totals as a joined string of formatted amounts.
export function renderCurrencyTotals(
  terminal: Terminal,
  totals: Record<string, number> | null | undefined,
  separator: string = '/n',
): string {
  if (!totals || Object.keys(totals).length === 0) {
    return '<span class="text-gray-400">No data</span>';
  }
  return Object.entries(totals)
    .map(([iso, value]) => formatCurrencyByISO(terminal, value, iso))
    .join(separator);
}

// Switch the visible tab inside the terminal detail modal.
// tabIndex 0=Overview, 1=Logs, 2=Settings, 3=Commands, 4=Accounting.
// Hooked into log polling: starts on tab 1, stops on any other tab.
//
// Lazy + dirty-aware model:
//   - On every switch we unhide the destination pane and update the
//     tab-button highlight.
//   - We call renderTab(n) to re-paint the destination tab from live
//     state -- UNLESS tab n is in state.modalDirtyTabs, in which case
//     we skip the re-paint and keep the existing DOM (which contains
//     the user's in-progress edits).
//   - The first switch to a tab always paints (dirty check applies
//     to "subsequent" switches, since the first time we always
//     start from a clean DOM after openTerminalImpl / saveSettings
//     cleared the dirty bit).
//   - Tabs 0/1/3/4 are not currently marked dirty by any code path
//     -- only tab 2 (Settings) tracks dirty state, via setPendingSetting
//     and setPendingPrice. saveSettings clears the dirty bit on
//     successful POST; closeModal / openTerminalImpl clear all bits.
export function switchModalTab(tabIndex: number): void {
  document.querySelectorAll('[id^="modal-tab-"]').forEach(el => el.classList.add('hidden'));
  document.querySelectorAll('.modal-tab').forEach(el => {
    el.classList.remove('border-b-2', 'border-emerald-500', 'text-emerald-600');
    el.classList.add('text-gray-500');
  });
  const content = document.getElementById(`modal-tab-${tabIndex}`);
  if (content) content.classList.remove('hidden');
  const activeTab = document.getElementById(`tab-${tabIndex}`);
  if (activeTab) {
    activeTab.classList.add('border-b-2', 'border-emerald-500', 'text-emerald-600');
    activeTab.classList.remove('text-gray-500');
  }
  // Re-paint the destination tab so the user always sees fresh data,
  // UNLESS the tab is dirty (has in-progress user input). renderTab()
  // is also a no-op if state.currentTerminal is missing.
  const state = liveState();
  if (!state.modalDirtyTabs || !state.modalDirtyTabs.has(tabIndex)) {
    renderTab(tabIndex);
  }

  // Feature 3: log polling -- tab 1 = Logs
  const startPoll = win().startLogPolling;
  const stopPoll = win().stopLogPolling;
  if (tabIndex === 1) {
    if (typeof startPoll === 'function') (startPoll as () => void)();
  } else {
    if (typeof stopPoll === 'function') (stopPoll as () => void)();
  }
}

// Per-tab render dispatcher. Reads state.currentTerminal; if missing,
// no-op (caller probably called this before openTerminalImpl set the
// pointer). Exposed as window.renderTab so other code (e.g. saveSettings
// after a successful POST) can refresh individual tabs.
export function renderTab(tabIndex: number): void {
  const t = liveState().currentTerminal;
  if (!t) return;
  const state = liveState();
  switch (tabIndex) {
    case 0: renderTabOverview(t, state); break;
    case 1: renderTabLogs(t, state);    break;
    case 2: renderTabSettings(t, state); break;
    case 3: renderTabCommands(t, state); break;
    case 4: renderTabAccounting(t, state); break;
    default: break;
  }
}

// Mark a setting as pending (user changed it but hasn't saved yet).
// Mutates state.currentTerminal.set_send and adds orange highlight to
// the parent card. Uses the global `event` (inline onclick context).
export function setPendingSetting(
  key: string,
  value: unknown,
  state: TimestopState
): void {
  const t = state.currentTerminal;
  if (!t) return;
  // Phase 10.2b: this is a Settings-tab edit, so mark tab 2 dirty.
  // switchModalTab(2) will skip the re-paint next time the user
  // navigates away and back, preserving the in-progress input.
  if (state.modalDirtyTabs) state.modalDirtyTabs.add(2);
  if (!t.set_recv) t.set_recv = {};
  // Queue ONLY the fields the operator touched. Seeding set_save from
  // set_recv (the old behaviour) pushed the whole settings blob --
  // prices, currency table, precision, demo_time -- at the cabinet on
  // every save, and the bets are read-only here. saveSettings() fills
  // the three editable keys from recv for the ones left untouched.
  if (!t.set_save) t.set_save = {};
  (t.set_save as Record<string, unknown>)[key] = value;
  // Add orange class to the parent card via selectSiblings -- toggle
  // the 'unsaved' class on the input's parent card so siblings lose
  // the highlight (only one card shows 'unsaved' at a time).

  const ev = win().event as Event | undefined;
  const target = ev?.target as HTMLElement | null;

  if (!target) return;
  selectSiblings(target, ['unsaved']);
}

// setRuntime -- per-terminal "Operation" toggle in the Settings
// tab. Writes the value to the top-level `runtime` column on
// `mach` via /api/terminal_set_runtime (admin-only).
//
// Unlike the other Settings tab buttons (frozen, force_sync,
// buy_mode, prices) this one is NOT batched into the "Save All
// Changes" pipeline: the column is a deployment-level flag, not
// part of the spot-terminal's set_send sync protocol. Each
// click round-trips immediately so the operator gets an
// immediate visual feedback (the dashed-orange border is
// added/removed on the card + modal right after the POST
// returns 200).
//
// On success:
//   1. t.runtime is updated in place so the next renderTab(2)
//      paints the new active button.
//   2. The `.testing` class is toggled on the modal body and
//      the All Terminals / Quick view card right away (the
//      next /api/data refresh will catch the rest of the
//      cards eventually, but we don't want to wait for the
//      30s ticker for a single click).
export async function setRuntime(value: number): Promise<void> {
  const state = liveState();
  const t = state.currentTerminal;
  if (!t) return;
  if (value !== 0 && value !== 1) return;

  // Optimistic UI: flip the class on the visible card / modal
  // immediately, then await the POST. If the POST fails we
  // revert both the class and the in-memory t.runtime.
  const previousRuntime = t.runtime;
  t.runtime = value;
  applyTestingClass(t, state);

  try {
    const config = (win().__timestop_config as ApiFetchConfig | undefined) || {
      apiBase: '/api/', csrfToken: (state as unknown as { csrfToken: string | null }).csrfToken,
    };
    const r = await apiFetch('terminal_set_runtime', {
      method: 'POST',
      body: { mid: t.mid, runtime: value },
    }, config);
    if (!r.ok) {
      // Roll back on any non-2xx (forbidden, missing mid,
      // invalid_runtime, db_error, ...).
      t.runtime = previousRuntime;
      applyTestingClass(t, state);
      const err = (r.data as { error?: string })?.error || 'unknown';
      console.warn('[setRuntime] server rejected', r.status, err);
      alert('Failed to set runtime: ' + err);
      return;
    }
    // 200: re-paint the Settings tab so the active button
    // matches the new value (the optimistic in-memory flip
    // already drives the card/modal border; this refreshes
    // the button styling).
    const w = win();
    if (typeof w.renderTabSettings === 'function') {
      (w.renderTabSettings as (t: Terminal, s: typeof state) => void)(t, state);
    }
  } catch (e) {
    // Network error: roll back.
    t.runtime = previousRuntime;
    applyTestingClass(t, state);
    console.warn('[setRuntime] network error', e);
    alert('Failed to set runtime: ' + (e as Error).message);
  }
}

// applyTestingClass -- adds/removes the .testing class on
// the openTerminal modal body AND the matching card in the
// All Terminals and Quick view lists so the dashed-orange
// border tracks the new runtime value without waiting for a
// full re-fetch.
function applyTestingClass(t: Terminal, state: TimestopState): void {
  const isTesting = Number(t.runtime) === 0;
  const modalBody = document.querySelector('#modal-backdrop .modal');
  if (modalBody) {
    if (isTesting) modalBody.classList.add('testing');
    else modalBody.classList.remove('testing');
  }
  // Re-render both card views so the .testing class on the
  // matching card is in sync with the new value. Cheap
  // re-paint; the cards are already in the DOM.
  const w = win();
  if (typeof w.renderQuickTerminals === 'function') {
    (w.renderQuickTerminals as (s: typeof state) => void)(state);
  }
  if (typeof w.renderAllTerminals === 'function') {
    (w.renderAllTerminals as (s: typeof state) => void)(state);
  }
}

// Mark a hopper-price input as pending. Mutates
// t.set_save.prices[`h${hopper}`][idx] = value. Adds 'pending unsaved'
// class to the input's parent card so the user sees the orange
// highlight. (The Save All Changes button picks up the change because
// t.set_save is what saveSettings reads.)
//
// hopper: 1, 2, or 3 (which hopper's price is being changed)
// idx:    0-based currency index (matches t.cur_recv array order)
// value:  the new price from the input (string, will be coerced to
//         number). Sent in *minor units* (server stores prices in
//         minor units; the input shows them already divided by the
//         currency's `div` factor -- so we multiply back here).
//
// Uses the global `event` (inline onchange context) to find the
// input element and add the 'pending unsaved' class via selectSiblings.
export function setPendingPrice(
  hopper: number,
  idx: number,
  value: string,
  state: TimestopState
): void {
  const t = state.currentTerminal;
  if (!t) return;
  // Phase 10.2b: this is a Settings-tab edit, so mark tab 2 dirty.
  // See setPendingSetting for the same pattern.
  if (state.modalDirtyTabs) state.modalDirtyTabs.add(2);
  if (!t.set_recv) t.set_recv = {};
  if (!t.set_send) t.set_send = t.set_recv;
  if (!t.set_save) t.set_save = t.set_send;

  // Ensure t.set_save.prices is a { h1: number[], h2: number[], h3: number[] } map
  const saveObj = t.set_save as Record<string, unknown>;
  let prices = saveObj.prices as Record<string, number[] | undefined> | undefined;
  if (!prices || typeof prices !== 'object') {
    prices = { h1: [], h2: [], h3: [] };
    saveObj.prices = prices;
  }
  const hKey = 'h' + hopper;
  const arr = prices[hKey] ? prices[hKey]!.slice() : [];
  // Multiply by the currency's `div` factor (input is human-readable;
  // server stores in minor units). Look up the currency for this idx.
  const currencies = Object.values(t.cur_recv || {});
  const cur = currencies[idx] as Record<string, unknown> | undefined;
  const divider = Number(cur?.div ?? 1);
  const num = Number(value);
  arr[idx] = isFinite(num) ? Math.round(num * divider) : 0;
  prices[hKey] = arr;

  // Highlight the parent card
  const ev = win().event as Event | undefined;
  const target = ev?.target as HTMLElement | null;
  if (target) {
    const card = target.closest('div');
    if (card) {
      // Add 'pending unsaved' to this card, remove from siblings so
      // only the latest edit highlights
      const parent = card.parentElement;
      if (parent) {
        Array.from(parent.children).forEach(sib => {
          if (sib !== card) {
            sib.classList.remove('pending', 'unsaved');
          }
        });
      }
      card.classList.add('pending', 'unsaved');
    }
  }
}
// setPendingBetValue -- Settings tab "Bets" inputs. The real cabinet
// reports its bet table as flat scalars inside `settings.prices`:
//   p1/p2/p3  -- price of bet 1/2/3, in minor units
//   t1/t2/t3  -- how many tokens a bet 1/2/3 pays out
// (the old sample shape `prices.h1..h3 = [per-currency arrays]` is not
// what the firmware sends, so the grid no longer reads it).
//
// field: 'p' (price) or 't' (tokens per bet)
// bet:   1 | 2 | 3
// value: raw input string -- major units for 'p' (multiplied back by the
//        first currency's `div`), a plain count for 't'
export function setPendingBetValue(
  field: 'p' | 't',
  bet: number,
  value: string,
  state: TimestopState
): void {
  const t = state.currentTerminal;
  if (!t) return;
  if (state.modalDirtyTabs) state.modalDirtyTabs.add(2);
  if (!t.set_recv) t.set_recv = {};
  if (!t.set_send) t.set_send = t.set_recv;
  if (!t.set_save) t.set_save = t.set_send;

  const saveObj = t.set_save as Record<string, unknown>;
  let prices = saveObj.prices as Record<string, number> | undefined;
  if (!prices || typeof prices !== 'object') {
    prices = {};
    saveObj.prices = prices;
  }
  const num = Number(value);
  const key = field + bet;
  if (field === 'p') {
    const currencies = Object.values(t.cur_recv || {});
    const div = Number((currencies[0] as Record<string, unknown> | undefined)?.div ?? 1) || 1;
    prices[key] = isFinite(num) ? Math.round(num * div) : 0;
  } else {
    prices[key] = isFinite(num) ? Math.round(num) : 0;
  }

  const ev = win().event as Event | undefined;
  const target = ev?.target as HTMLElement | null;
  if (target) {
    const card = target.closest('div');
    if (card) card.classList.add('pending', 'unsaved');
  }
}

function selectSiblings(el: HTMLElement, Classes: string[]) {
  for (const sib of el.parentElement?.children ?? []) {
    if (sib === el) continue;
    sib.classList.remove(...Classes);
  }
  el.classList.add(...Classes);
}

// Two calling styles:
//   1. Legacy: showConfirm(state, cmd, val, config, apiFetchFn)
//      -- hardcodes the title/message and the POST-to-terminal_cmd behavior
//   2. Generic: showConfirm(state, { title, message, onYes })
//      -- caller provides the title, message, and what Yes does
export function showConfirm(
  state: TimestopState,
  cmdOrOpts: string | { title: string; message: string; onYes: () => void | Promise<void> },
  val?: string,
  config?: ApiFetchConfig,
  apiFetchFn?: ApiFetchFn,
): void {
  const t = state.currentTerminal;
  if (!t) return;
  const modal = document.getElementById('confirm-modal');
  const title = document.getElementById('confirm-title');
  const msg = document.getElementById('confirm-message');
  const yesBtn = document.getElementById('confirm-yes') as HTMLButtonElement | null;
  const noBtn = document.getElementById('confirm-no') as HTMLButtonElement | null;
  if (!modal || !title || !msg || !yesBtn || !noBtn) return;

  // Generic style: { title, message, onYes }
  if (typeof cmdOrOpts === 'object' && cmdOrOpts !== null) {
    title.textContent = cmdOrOpts.title;
    msg.textContent = cmdOrOpts.message;
    yesBtn.onclick = async () => {
      modal.classList.add('hidden');
      // Reset cmd-btn highlight state -- the user confirmed, so the
      // "pending confirmation" orange is no longer accurate. The
      // command is now in t.cmd_send (or will be shortly), and the
      // button will re-render with the `pending unsaved` classes on
      // the next renderTab(3) pass.
      document.querySelectorAll('.cmd-btn.unsaved').forEach(b => {
        //b.classList.remove('unsaved');
      });
      await cmdOrOpts.onYes();
    };
    noBtn.onclick = () => {
      modal.classList.add('hidden');
      // User cancelled -- drop the unsaved class entirely so the
      // button returns to its default state.
      document.querySelectorAll('.cmd-btn.unsaved').forEach(b => {
        //b.classList.remove('unsaved');
      });
    };
    modal.classList.remove('hidden');
    return;
  }

  // Legacy style: (state, cmd, val, config, apiFetchFn)
  const cmd = cmdOrOpts;
  const cmdLabel = cmd === 'reboot' ? i18n('tab_commands.reboot') : i18n('tab_commands.payout_n', { n: val });
  title.textContent = cmdLabel + '?';
  msg.textContent = cmd === 'reboot'
    ? i18n('tab_commands.reboot_confirm_message')
    : i18n('tab_commands.payout_confirm_message', { n: val });

  const payload: Record<string, unknown> = { command: { [cmd]: parseInt(val ?? '1', 10) || 1 } };
  const jsonStr = JSON.stringify(payload);

  yesBtn.onclick = async () => {
    modal.classList.add('hidden');
    // Reset cmd-btn highlight state -- the user confirmed.
    document.querySelectorAll('.cmd-btn.unsaved').forEach(b => {
      //b.classList.remove('unsaved');
    });
    if (!t.cmd_send) (t as Record<string, unknown>).cmd_send = {};
    const sendCmd = (t.cmd_send as Record<string, unknown>);
    if (!sendCmd.command) sendCmd.command = {};
    (sendCmd.command as Record<string, unknown>)[cmd] = parseInt(val ?? '1', 10) || 1;
    (t as Record<string, unknown>)._last_cmd_sent = { cmd, val, json: jsonStr, ts: Date.now() };

    if (config && apiFetchFn) {
      try {
        await apiFetchFn('terminal_cmd',
          {
            method: 'POST',
            body: { mid: t.mid, command: payload.command, raw: jsonStr },
          }, config);
        console.log(i18n('tab_commands.cmd_sent') + ':', jsonStr);
      } catch (e) {
        console.warn('cmd post failed (continuing anyway):', (e as Error).message);
      }
      // Start auto-refresh of hopper levels
      startHopperRefresh(state, config, apiFetchFn);
    }
  };
  noBtn.onclick = () => {
    modal.classList.add('hidden');
    // User cancelled -- drop the unsaved class entirely.
    document.querySelectorAll('.cmd-btn.unsaved').forEach(b => {
      //b.classList.remove('unsaved');
    });
  };
  modal.classList.remove('hidden');
}

// Refresh hopper levels by re-fetching terminal data every 3s for 30s.
export function startHopperRefresh(
  state: TimestopState,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
): void {
  if (state.hopperRefreshTimer) clearInterval(state.hopperRefreshTimer);
  const status = document.getElementById('cmd-refresh-status');
  if (status) status.classList.remove('hidden');

  let ticks = 0;
  const maxTicks = 10;

  const refresh = async (): Promise<void> => {
    const t = state.currentTerminal;
    if (!t) { stopHopperRefresh(state); return; }
    try {
      await apiFetchFn(`terminal?id=${encodeURIComponent(String(t.id))}`, { method: 'GET' }, config);
    } catch { /* network blip */ }
    try {
      const r = await apiFetchFn('data', {}, config);
      if (r.ok && r.data) {
        const dataObj = r.data as Record<string, unknown>;
        const terms = dataObj.terminals as Record<string, Terminal> | undefined;
        if (terms) {
          const updated = Object.values(terms).find(x => x.mid === t.mid);
          if (updated) {
            t.bnr_recv = updated.bnr_recv;
            updateHopperLevelsDisplay(state);
          }
        }
      }
    } catch { /* ignore */ }
    ticks++;
    if (ticks >= maxTicks) stopHopperRefresh(state);
  };

  refresh();
  state.hopperRefreshTimer = setInterval(() => { void refresh(); }, 3000) as unknown as number;
}

export function stopHopperRefresh(state: TimestopState): void {
  if (state.hopperRefreshTimer) {
    clearInterval(state.hopperRefreshTimer);
    state.hopperRefreshTimer = null;
  }
  const status = document.getElementById('cmd-refresh-status');
  if (status) status.classList.add('hidden');
}

export function updateHopperLevelsDisplay(state: TimestopState): void {
  const t = state.currentTerminal;
  let bnrData = { balances: [0, 0, 0], refill: [0, 0, 0] };
  if (t && t.bnr_recv) {
    try {
      const raw = typeof t.bnr_recv === 'string'
        ? JSON.parse(t.bnr_recv as string)
        : t.bnr_recv;
      if (raw && Array.isArray(raw.balances)) bnrData.balances = raw.balances;
      if (raw && Array.isArray(raw.refill)) bnrData.refill = raw.refill;
    } catch { /* keep zeros */ }
  }
  // Per-cell IDs are painted in BOTH renderTabOverview and
  // renderTabCommands (identical markup), so writing to all of them
  // keeps both tabs in sync. The old single-bucket #modal-hoplvl /
  // #modal-hopout IDs are gone -- the new layout uses one cell per
  // hopper / per currency.
  const currencies = t ? Object.values(t.cur_recv || {}) : [];
  for (let i = 0; i < 3; i++) {
    const lvl = document.getElementById('modal-hoplvl' + i);
    if (lvl) lvl.textContent = String(bnrData.refill[i] ?? 0);
    const bal = document.getElementById('modal-bal' + i);
    if (bal) {
      const cur = currencies[i] as Record<string, unknown> | undefined;
      const iso = (cur?.ISO as string | undefined) || ('C' + i);
      bal.textContent = t ? formatCurrencyByISO(t, bnrData.balances[i] ?? 0, iso) : String(bnrData.balances[i] ?? 0);
    }
  }
}

// Edit a terminal field (name or location) in place via prompt().
// Shows orange frame during round-trip, then clears it.
export async function editTerminalField(
  state: TimestopState,
  field: string,
  el: HTMLElement,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
): Promise<void> {
  const t = state.currentTerminal;
  if (!t) return;
  const currentValue = String((t as Record<string, unknown>)[field] || '');
  const newValue = prompt(i18n('errors.edit_field_prompt', { field }), currentValue);
  if (newValue === null) return;
  if (newValue === currentValue) return;

  const originalClasses = el.className;
  el.classList.add('border-b-2', 'border-orange-500', 'text-orange-600');
  const originalText = el.innerText;
  el.innerText = newValue;

  try {
    const r = await apiFetchFn('terminal_update', {
      method: 'POST',
      body: { mid: t.mid, fields: { [field]: newValue } },
    }, config);
    if (!r.ok) {
      el.innerText = originalText;
      el.className = originalClasses;
      throw new Error((r.data as Record<string, unknown> | null)?.error as string || 'save_failed');
    }
    (t as Record<string, unknown>)[field] = newValue;
    el.classList.remove('border-b-2', 'border-orange-500', 'text-orange-600');
    const rq = win().renderQuickTerminals;
    const ra = win().renderAllTerminals;
    if (typeof rq === 'function') (rq as (s: TimestopState) => void)(state);
    if (typeof ra === 'function') (ra as (s: TimestopState) => void)(state);
    console.log(i18n('common.save') + ' ' + field + ' = "' + newValue + '"');
  } catch (e) {
    alert(i18n('errors.save_failed', { message: i18n('common.save') + ' ' + field + ': ' + (e as Error).message }));
  }
}

// Close the terminal detail modal. Stops log polling, hopper refresh,
// destroys chart instance, resets currentTerminal.
export function closeModal(state: TimestopState): void {
  const stopPoll = win().stopLogPolling;
  if (typeof stopPoll === 'function') (stopPoll as () => void)();
  document.getElementById('modal-backdrop')?.classList.add('hidden');
  document.getElementById('modal-backdrop')?.classList.remove('flex');
  state.currentTerminal = null;
  if (state.modalChartInstance) {
    (state.modalChartInstance as { destroy: () => void }).destroy();
     state.modalChartInstance = null;
  }
  stopHopperRefresh(state);
  const cm = document.getElementById('confirm-modal');
  if (cm) cm.classList.add('hidden');
  // Phase 10.2b: clear all per-tab dirty bits. Closing the modal is
  // a clean slate -- any in-progress edits are abandoned, so the next
  // openTerminalImpl should re-paint from scratch.
  if (state.modalDirtyTabs) state.modalDirtyTabs.clear();
  // Phase 12.8: drop the Ranged accounting cache + selection so the
  // next terminal opens with an empty calendar.
  const crFn = win().clearRangedState;
  if (typeof crFn === 'function') (crFn as () => void)();
  // Phase 12.21: drop the cached IP/Location geo so the next
  // terminal opens with a clean slate (the IP card on the new
  // terminal would otherwise briefly show the old terminal's IP
  // before the fresh resolveAndShowIp() lands).
  const cipFn = win().clearIpGeoCache;
  if (typeof cipFn === 'function') (cipFn as () => void)();
}

// Toggle terminal status (online/offline) -- used by the status badge
// in the modal. Re-opens the modal to refresh, then re-renders lists.
export function toggleStatus(state: TimestopState): void {
  const t = state.currentTerminal;
  if (!t) return;
  t.status = t.status === 'online' ? 'offline' : 'online';
  const ot = win().openTerminal;
  if (typeof ot === 'function') (ot as (id: number | string) => void)(t.id);
  const rq = win().renderQuickTerminals;
  const ra = win().renderAllTerminals;
  if (typeof rq === 'function') (rq as (s: TimestopState) => void)(state);
  if (typeof ra === 'function') (ra as (s: TimestopState) => void)(state);
}

// Batch-save all pending settings (frozen, force_sync, buy_mode, prices)
// from currentTerminal.set_send to the server via terminal_set endpoint.
//
// Asks for Yes/No confirmation before doing the actual save.
export async function saveSettings(
  state: TimestopState,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
): Promise<void> {
  const t = state.currentTerminal;
  if (!t) return;
  if (!t.set_save || Object.keys(t.set_save).length === 0) {
    // Nothing to save -- skip the confirm modal entirely
    console.log('saveSettings: nothing pending, skipping');
    return;
  }

  // Build a human-readable summary of pending changes
  const pending = t.set_save;
  const summary = Object.entries(pending)
    .map(([k, v]) => '  - ' + k + ': ' + v)
    .join('\n');

  showConfirm(state, {
    title: i18n('tab_settings.save_confirm_title'),
    message: i18n('tab_settings.save_confirm_message', { terminal: (t.name || ('terminal ' + t.mid)) }),
    onYes: async () => {
      const saveBtn = document.getElementById('save-settings-btn') as HTMLButtonElement | null;
      const originalText = saveBtn ? saveBtn.textContent : i18n('common.save_all');

      if (saveBtn) {
        saveBtn.textContent = i18n('common.saving');
        saveBtn.disabled = true;
      }

      // The settings push carries only the editable fields: frozen,
      // force_sync, timer_value and demo_time. Mode and the bet prices
      // are read-only in the BO -- the cabinet owns them.
      const recv = (t.set_recv || {}) as Record<string, unknown>;
      const pendingSave = (t.set_save || {}) as Record<string, unknown>;
      // frozen / force_sync / mode are flags; timer_value is the win
      // window in whole seconds (3..10) and demo_time the attract
      // countdown (0 = off, 10..360 in 10 s steps). Both are clamped
      // server-side of the UI too, so a hand-typed value can't leave
      // the range the cabinet accepts.
      const settingsSpec: Array<[string, (n: number) => number]> = [
        ['frozen',      (n) => (n ? 1 : 0)],
        ['force_sync',  (n) => (n ? 1 : 0)],
        ['timer_value', (n) => Math.min(10, Math.max(3, Math.round(n)))],
        ['demo_time',   (n) => Math.min(360, Math.max(0, Math.round(n / 10) * 10))],
      ];
      const settings: Record<string, unknown> = {};
      for (const [key, clamp] of settingsSpec) {
        const v = hasOwn(pendingSave, key) ? pendingSave[key] : recv[key];
        if (v !== undefined && v !== null && v !== '') settings[key] = clamp(Number(v));
      }

      const payload = {
        mid: t.mid,
        set_send: settings,
      };

      try {
        const r = await apiFetchFn('terminal_set', { method: 'POST', body: payload }, config);
        if (!r.ok) {
          throw new Error((r.data as Record<string, unknown> | null)?.error as string || 'save_failed');
        }
        t.set_send = t.set_save;
        t.set_save = {};
        // Phase 10.2b: save succeeded, so Settings tab is no longer
        // dirty. Clear the bit so the next switch back to tab 2 will
        // re-paint from the fresh server state.
        if (state.modalDirtyTabs) state.modalDirtyTabs.delete(2);
        if (saveBtn) saveBtn.textContent = i18n('common.saved');

        setTimeout(() => {
          if (saveBtn) {
            saveBtn.textContent = originalText;
            saveBtn.disabled = false;
          }

          closeModal(state);
          const rq = win().renderQuickTerminals;
          const ra = win().renderAllTerminals;
          if (typeof rq === 'function') (rq as (s: TimestopState) => void)(state);
          if (typeof ra === 'function') (ra as (s: TimestopState) => void)(state);
        }, 900);
      } catch (e) {
        alert(i18n('tab_settings.save_failed', { message: (e as Error).message }));
        if (saveBtn) {
          saveBtn.textContent = originalText;
          saveBtn.disabled = false;
        }
      }
    },
  });
}

// =====================================================================
// openTerminalImpl -- entry point from render.ts click handlers
// =====================================================================
//
// Async: fetches the latest data for `id` (so the user sees fresh
// status / hopper levels / logs, not whatever fetchData() loaded
// minutes ago), then opens the modal. Spinner covers the WHOLE app
// from the moment the user clicks a terminal card until the modal
// is painted — see the spinner-backdrop z-120 in admin/index.php.
//
// Flow:
//   1. showSpinner('Loading terminal data...')  -- visible IMMEDIATELY
//      on the same tick the onclick handler fires
//   2. await apiFetch('terminal?id=N', ...)    -- 200-1500ms server
//      roundtrip; spinner is visible the whole time
//   3. Object.assign(state.terminals[mid], fresh)  -- merge into the
//      shared state object so renderers (inline + bundle) see fresh
//      data
//   4. rAF #1: paintTerminalModal(t, state)   -- the heavy DOM work
//   5. rAF #2: hideSpinner()                   -- spinner hides, modal
//      already painted behind it
//   6. rAF #3: switchModalTab(0) to highlight Overview
//
// If the fetch fails (network error, 5xx, etc.) we still open the
// modal with the stale data -- the user gets to see something rather
// than a frozen spinner. The error is logged but not surfaced in UI.
//
// Other tabs (Logs/Settings/Commands/Accounting) are NOT painted on
// open -- they're painted lazily on first activation by switchModalTab.
export async function openTerminalImpl(
  id: number | string,
  state: TimestopState,
  config: { apiBase: string; csrfToken: string | null },
): Promise<void> {
  // Phase 10.2b: clear all dirty bits for the new terminal. The
  // previous terminal's in-progress edits are abandoned (we don't
  // warn about this; consistent with the existing behaviour of
  // openTerminal not preserving edits). switchModalTab will now
  // re-paint the Settings tab from scratch on first activation.
  if (state.modalDirtyTabs) state.modalDirtyTabs.clear();

  // Show the spinner BEFORE the await so the user sees it the moment
  // the click registers. The full-page z-120 backdrop covers every
  // other modal (login, confirm, add, terminal) and the rest of the
  // app shell, so the user can't click anything else while the fetch
  // is in flight.
  showSpinner(i18n('terminal_modal.loading_terminal_data'));

  // Refetch the terminal's fresh data. We do this even if the terminal
  // is already in state.terminals (which it usually is) so the modal
  // shows the latest status / hopper / log entries rather than whatever
  // the last fetchData() tick loaded.
  try {
    const r = await apiFetch(`terminal?id=${encodeURIComponent(String(id))}`, { method: 'GET' }, config);
    if (r.ok && r.data && typeof r.data === 'object') {
      const fresh = r.data as Record<string, unknown>;
      // The state.terminals dictionary is keyed by id (numeric string),
      // not by mid. Older revisions of this code keyed by mid and
      // created a duplicate entry on every open, leaving the
      // dashboard's id-keyed copy stale and the .find() below picking
      // the wrong one. Find by id and assign in place so every
      // reference (state.terminals[id], state.currentTerminal, the
      // dashboard renderers) sees the same updated object.
      const existing = Object.values(state.terminals).find(
        (x) => (x as { id: unknown }).id === id,
      ) as Record<string, unknown> | undefined;
      if (existing) {
        Object.assign(existing, fresh);
      } else {
        // First time we've seen this id (rare — only after a fresh
        // login where /api/data hasn't loaded yet). Index by id.
        // The cast is necessary because state.terminals is typed
        // as Record<string, Terminal>, but the fresh response from
        // /api/terminal is untyped at this point.
        state.terminals[String(id)] = fresh as unknown as Terminal;
      }
      // Drop any mid-keyed orphan from a previous open (defensive
      // cleanup: the state can carry an orphan if the user upgraded
      // mid-session from an older build). The id-keyed entry is the
      // canonical reference; the mid key is just an alternate index
      // we keep in sync for the rare "we have mid but not id" path.
      const mid = fresh.mid != null ? String(fresh.mid) : '';
      if (mid && state.terminals[mid] && state.terminals[mid] !== existing) {
        // If the mid-keyed entry is a different object, replace it
        // with a reference to the id-keyed entry (so .find() by mid
        // also returns the canonical object). We don't `delete` it
        // because some renderers might still hold a reference to the
        // mid key; aliasing to the canonical object is safe and
        // prevents the duplicate from drifting again.
        if (existing) {
          state.terminals[mid] = existing as unknown as Terminal;
        } else {
          state.terminals[mid] = fresh as unknown as Terminal;
        }
      }
    }
  } catch (e) {
    // Network error / timeout / 5xx -- log and continue. Showing the
    // modal with stale data is better than freezing the page.
    console.warn('[openTerminalImpl] fetch error (showing stale data):', e);
  }

  // Resolve the terminal AFTER the assign so we get the freshest fields.
  const t = Object.values(state.terminals).find((x) => (x as { id: unknown }).id === id);
  if (!t) {
    // Server doesn't know about this id -- abort and clear the spinner.
    hideSpinner();
    return;
  }
  state.currentTerminal = t;

  // Defer the modal reveal one frame so the spinner actually paints
  // (without this the whole body runs synchronously and the spinner
  // never gets a chance to render before hideSpinner() runs). Use
  // awaited Promises so the outer `async` actually waits for the
  // entire paint cycle -- callers that `await openTerminalImpl`
  // can rely on the modal being fully painted and the spinner
  // hidden by the time the promise resolves.
  await new Promise<void>((rAF1) => {
    requestAnimationFrame(() => {
      paintTerminalModal(t, state);
      requestAnimationFrame(() => rAF1());
    });
  });
  hideSpinner();
  // Highlight the Overview tab on the freshly-painted chrome.
  // switchModalTab reads the dirty-bit set (empty here) and
  // re-paints, which is fine -- tab 0 was just painted.
  switchModalTab(0);
}

// paintTerminalModal -- the modal chrome (name, location, icon, status,
// hwid) plus the initial Overview tab. The other 4 tabs are NOT painted
// here; they're rendered on first activation by switchModalTab via the
// renderTab(n) dispatcher.
function paintTerminalModal(t: Terminal, state: TimestopState): void {
  prepareTerminal(t);

  const backdrop = document.getElementById('modal-backdrop');
  if (backdrop) {
    backdrop.classList.remove('hidden');
    backdrop.classList.add('flex');
  }

  // runtime=0 (Testing) adds a dashed-orange border on the
  // modal body. The .testing class is styled in admin/index.php
  // (border-left/right 5px dashed orange) and toggled on every
  // paint so the modal always reflects the current runtime
  // value (open → close → open a different terminal). Same
  // class is used by the All Terminals / Quick view cards.
  const modalBody = backdrop?.querySelector('.modal') as HTMLElement | null;
  if (modalBody) {
    if (Number(t.runtime) === 0) modalBody.classList.add('testing');
    else modalBody.classList.remove('testing');
  }

  const modalName = document.getElementById('modal-name');
  if (modalName) modalName.innerText = displayTerminalName(t);
  const modalLoc = document.getElementById('modal-location');
  if (modalLoc) modalLoc.innerText = t.location || '';

  const modalIcon = document.getElementById('modal-name-icon');
  const modalHwid = document.getElementById('modal-hwid');
  if (modalIcon) modalIcon.textContent = displayTerminalIcon(t);
  if (modalHwid) {
    const ver = (t as Record<string, unknown>).version;
    modalHwid.textContent = ver
      ? i18n('common.terminal_id_with_ver', { id: t.mid, ver: String(ver) })
      : i18n('common.terminal_id_no_ver', { id: t.mid });
  }

  // Status badge
  const statusEl = document.getElementById('modal-status');
  if (statusEl) {
    statusEl.innerHTML = t.status === 'online'
      ? '<span class="inline-flex items-center gap-x-1 bg-emerald-100 text-emerald-700 text-xs px-4 h-8 rounded-3xl"><span class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> ' + i18n('tab_overview.status_online') + '</span>'
      : '<span class="inline-flex items-center gap-x-1 bg-red-100 text-red-700 text-xs px-4 h-8 rounded-3xl">' + i18n('tab_overview.status_offline') + '</span>';
  }

  // Paint the Overview tab on open. Other tabs are lazy.
  // We DO NOT call renderTab(0) here — openTerminalImpl() calls
  // switchModalTab(0) right after this function returns, which
  // re-paints tab 0 (and is the only place a chart instance gets
  // created per modal open). Calling renderTab(0) here would
  // create a SECOND chart that's immediately destroyed by
  // switchModalTab's own renderTabOverview call, leaking the
  // orphan to Chart.js's internal registry. The modal body
  // chrome (name, location, status, etc.) is painted by this
  // function; the tab content stays empty until the subsequent
  // switchModalTab(0) lands. The spinner hides against the
  // painted chrome, not the tab body, so the user sees the modal
  // outline immediately and the tab content a frame later.
  // The Overview tab is the default. openTerminalImpl() calls
  // switchModalTab(0) right after this function returns, which
  // is the single source of the Overview tab paint (and the
  // single source of the chart instance creation). Calling
  // switchModalTab(0) here too would create a SECOND chart that
  // is immediately destroyed when the outer switchModalTab(0)
  // re-paints the tab — Chart.js's internal registry leaks the
  // orphan because the underlying canvas was already replaced.
}

// =====================================================================
// Per-tab render functions
// =====================================================================
//
// Each function reads state.currentTerminal, builds innerHTML for one
// tab pane, and wires up any per-tab event listeners. They are the
// public API of this file (along with renderTab) -- callers can:
//   - trigger a tab paint from window: window.renderTab(2)
//   - trigger a specific tab: window.renderTabSettings(t, state)
//
// All five are no-op safe: if state.currentTerminal is missing, return
// early. Tab 2 (Settings) is destructive on re-paint -- it wipes any
// in-progress user input. Callers that want to preserve input should
// not call switchModalTab(2) or renderTab(2) while a user has unsaved
// edits. (Future enhancement: track a per-tab "dirty" flag and guard
// the re-paint.)

// renderTabOverview -- paints the Overview tab (hopper levels, balances,
// total IN, hardware info, created/last-seen, IP, sales-trend chart).
// Calls renderTerminalLogs for any global-data side effects? No -- Logs
// are tab 1. Overview owns the chart instance lifecycle.
export function renderTabOverview(t: Terminal, state: TimestopState): void {
  // Parse bnr_recv
  let bnrData = { balances: [0, 0, 0], refill: [0, 0, 0] };
  if (t.bnr_recv) {
    try {
      const raw = typeof t.bnr_recv === 'string' ? JSON.parse(t.bnr_recv as string) : t.bnr_recv;
      if (raw && Array.isArray(raw.balances)) bnrData.balances = raw.balances;
      if (raw && Array.isArray(raw.refill)) bnrData.refill = raw.refill;
    } catch { /* keep zeros */ }
  }

  const currencies = Object.values(t.cur_recv || {});
  const totalin = getTotalByCurrency(t, 'acc_shor');
  // "DEVICES" replaced the old "Money in" + "Hoppers" pair: one row
  // listing only the devices that touch cash — the coin validator,
  // the note validator and the (single) dispenser hopper. The three
  // hopper slots are NOT listed separately; the per-hopper detail
  // stays in the "Hopper levels" block above. The hw_info key we read
  // for the hopper is still hopper1; the label override surfaces the
  // raw field name as a tiny subtext so the operator can trace the
  // value back to mach.hw_info without ambiguity.
  // devCell takes an optional display label override, so a field can
  // show up with a friendlier label in the UI without changing the
  // hw_info key we read.
  const devCell = (dev: string, label?: string): string => {
    const hw = (t as Record<string, unknown>).hw_info as Record<string, unknown> | undefined;
    const displayedValue = hw ? (hw[dev] || '___') : '___';
    const subtext = label && label !== dev
      ? '<div class="text-[9px] text-gray-400 -mt-0.5">' + dev + '</div>'
      : '';
    return '<div class="bg-gray-100 rounded-xl p-1 text-center"><div class="text-xs text-gray-500">' + (label || dev) + '</div>' + subtext + '<div class="font-semibold text-xs">' + displayedValue + '</div></div>';
  };

  // ---- Destroy any prior chart BEFORE we wipe the canvas ----
  // Order matters here. The old chart's underlying canvas gets
  // removed by the tab0.innerHTML = ... assignment below, but if
  // we destroy the chart AFTER that, Chart.js's internal registry
  // loses its handle on the canvas (it's no longer in the DOM) and
  // can leak the instance. Destroying FIRST keeps the chart's
  // canvas reference live for the cleanup, and is also why we
  // reach for state.modalChartInstance (the chart on the old
  // canvas) before assigning tab0.innerHTML.
  if (state.modalChartInstance) {
    try { (state.modalChartInstance as { destroy: () => void }).destroy(); } catch { /* already gone */ }
    state.modalChartInstance = null;
  }

  const tab0 = document.getElementById('modal-tab-0');
  if (tab0) {
    tab0.innerHTML = `
    <div class="space-y-4">
      <div class="bg-gray-100 rounded-2xl p-2">
        <div class="flex items-center justify-between gap-3 px-1">
          <span class="text-xs text-gray-500">${i18n('tab_overview.balances')}</span>
          <span class="font-semibold text-md text-right">
            ${currencies.map((cur, idx) => {
              const iso = cur.ISO || `C${idx}`;
              const displayedValue = formatCurrencyByISO(t, bnrData.balances[idx], iso);
              return `<div id="modal-bal${idx}" class="">${displayedValue}</div>`;
            }).join('')}
          </span>
        </div>
        <hr class="my-1 border-gray-200">
        <div class="flex items-center justify-between gap-3 px-1">
          <span class="text-xs text-gray-500">${i18n('tab_overview.total_in')}</span>
          <span class="font-semibold text-md text-right">
            ${currencies.map((cur, idx) => {
              const iso = cur.ISO || `C${idx}`;
              const displayedValue = formatDividedCurrencyByISO(t, totalin[iso] || 0, iso);
              return `<div id="modal-totalin${idx}" class="">${displayedValue}</div>`;
            }).join('')}
          </span>
        </div>
      </div>
      <div>
        <div class="flex items-center gap-2 mb-2">
          <span class="text-base">🖥️</span>
          <h5 class="text-xs font-semibold uppercase tracking-wider text-gray-500">${i18n('tab_overview.devices')}</h5>
        </div>
        <div class="grid grid-cols-3 gap-1">
          ${['coins', 'notes'].map(d => devCell(d)).join('')}
          ${devCell('hopper1', i18n('tab_overview.hopper_label'))}
        </div>
      </div>
      <div class="grid grid-cols-2 lg:grid-cols-2 gap-4">
        <div class="bg-gray-100 rounded-2xl p-4">
          <div class="text-xs text-gray-500">${i18n('tab_overview.date_created')}</div>
          <div id="ov-created" class="font-semibold text-gray-700 text-sm mt-1 break-words text-center">${i18n('tab_overview.unresolved')}</div>
        </div>
        <div class="bg-gray-100 rounded-2xl p-4">
          <div class="text-xs text-gray-500">${i18n('tab_overview.last_seen')}</div>
          <div id="ov-lastseen" class="font-semibold text-sm mt-1 break-words text-center">${i18n('tab_overview.unresolved')}</div>
        </div>
        </div>

      <div class="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <div id="ov-ip-card" class="bg-gray-100 rounded-2xl p-4">
          <div class="cursor-pointer select-none" onclick="expandIpDetails()">
            <div class="flex items-center justify-between">
              <div class="text-xs text-gray-500">${i18n('tab_overview.ip_location')}</div>
              <span id="ov-ip-expand" class="hidden text-xs text-gray-400 flex items-center gap-1">
                <span id="ov-ip-chevron">▸</span>
                <span>${i18n('tab_overview.details')}</span>
              </span>
            </div>
            <div id="ov-ip" class="font-semibold text-sm mt-1 break-words">${i18n('tab_overview.resolving')}</div>
          </div>
          <div id="ov-ip-details" class="hidden mt-3 pt-3 border-t border-gray-200"></div>
        </div>
      </div>
      <div>
        <h4 class="uppercase text-xs tracking-widest text-gray-400 mb-3">${i18n('tab_overview.sales_trend')}</h4>
        <!-- Fixed-height container: Chart.js with responsive:true +
             maintainAspectRatio:false sizes the canvas to its parent.
             Without an explicit parent height, the chart can grow on
             every re-paint (parent is auto, canvas height="90"
             attribute becomes the floor, and Chart.js expands to fit
             the natural intrinsic size — which on a 2nd paint is
             bigger than the 1st). A 120px wrapper is enough for a
             7-day line chart and pins the size across repaints. -->
        <div class="relative" style="height: 120px;">
          <canvas id="modalChart" height="90"></canvas>
        </div>
      </div>
    </div>`;
  }

  // Fill metadata row synchronously
  const tAny = t as Record<string, unknown>;
  const created = tAny.ts_crea || tAny.created_at || null;
  const driftVal = (state.globalData as Record<string, unknown>).drift_srv2local as number | null | undefined;
  const seen = lastSeenFor(t, driftVal ?? 0) || tAny.ts_serv || tAny.ts_seen || tAny.ts_spot || null;
  const ovCreated = document.getElementById('ov-created');
  if (ovCreated) ovCreated.textContent = formatTs(created);
  const ovSeen = document.getElementById('ov-lastseen');
  if (ovSeen) ovSeen.textContent = formatTs(seen);

  // Chart.js — Sales Trend (last 7 days). The data source is the
  // per-operator acc_{oid} history table for this terminal (the
  // same table the per-terminal Ranged tab pulls from), via
  // /api/terminalacc?id=N&from=...&to=... . We sum the 3 hopper
  // cnts (acc_diff.out[i].cnt) per day and render 7 points
  // (oldest -> today). The chart instance is rebuilt on every
  // renderTabOverview() call (defensive — the lazy switchModalTab
  // path also re-paints Overview, and we never want a stale chart
  // pointing at a destroyed canvas).
  const ctx = document.getElementById('modalChart') as HTMLCanvasElement | null;
  if (ctx) {
    // The prior chart instance was already destroyed at the top
    // of renderTabOverview (before tab0.innerHTML wiped the old
    // canvas), so we don't need to destroy it again here.
    // Render a flat-zero placeholder immediately so the user
    // sees the line shape even before the fetch resolves. The
    // actual data overlays once the response lands.
    const ChartCtor = win().Chart as
      | (new (ctx: HTMLCanvasElement, config: Record<string, unknown>) => { destroy: () => void })
      | undefined;
    if (ChartCtor) {
      // Build 7 day labels (oldest -> today) and a 7-slot data
      // array seeded with zeros. The fetch overwrites .data in
      // place once it returns; if the fetch fails or the table
      // doesn't exist, the flat line stays.
      const days: string[] = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const m = (d.getMonth() + 1 < 10 ? '0' : '') + (d.getMonth() + 1);
        const dd = (d.getDate() < 10 ? '0' : '') + d.getDate();
        days.push(m + '-' + dd);
      }
      const seedData = [0, 0, 0, 0, 0, 0, 0];
      const inst = new ChartCtor(ctx, {
        type: 'line',
        data: {
          labels: days,
          datasets: [{
            label: i18n('tab_overview.chart_label_tokens'),
            data: seedData,
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.12)',
            borderWidth: 3,
            tension: 0.3,
            pointRadius: 0,
            pointHoverRadius: 5,
            fill: true,
          }],
        },
        options: {
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { precision: 0 } },
            x: { grid: { color: '#f1f5f9' } },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
      state.modalChartInstance = inst;

      // ---- Async fetch of the last 7 days from acc_{oid} ----
      const cfg = win().__timestop_config as ApiFetchConfig | undefined || {
        apiBase: '/api/',
        csrfToken: state.csrfToken,
      };
      const from = days[0].replace(/^(\d{2})-(\d{2})$/, (m, mm, dd) => {
        // days[0] is MM-DD; need YYYY-MM-DD with current year.
        return today.getFullYear() + '-' + mm + '-' + dd;
      });
      const toYmd = today.getFullYear() + '-'
        + (today.getMonth() + 1 < 10 ? '0' : '') + (today.getMonth() + 1) + '-'
        + (today.getDate() < 10 ? '0' : '') + today.getDate();
      // Fire-and-forget. If it fails (terminal has no oid yet, the
      // acc_{oid} table doesn't exist, network blip), the flat-zero
      // seed stays in place — the chart still renders, just with no
      // data points. Better than a hard error.
      (async () => {
        try {
          const r = await apiFetch(
            'terminalacc?id=' + encodeURIComponent(String(t.id)) +
              '&from=' + encodeURIComponent(from) +
              '&to='   + encodeURIComponent(toYmd),
            { method: 'GET' },
            cfg,
          );
          if (!r || !r.ok || !r.data || typeof r.data !== 'object') return;
          const records = ((r.data as { records?: unknown[] }).records) || [];
          // Sum 3 hopper cnts per YYYY-MM-DD into a lookup. If a
          // day has multiple records (e.g. multiple resets), they
          // collapse to a single point for the chart.
          const byDay: Record<string, number> = {};
          for (const rec of records) {
            if (!rec || typeof rec !== 'object') continue;
            const r2 = rec as { dated?: string; acc_diff?: unknown };
            if (!r2.dated) continue;
            const acc = r2.acc_diff as { out?: Array<{ cnt?: number }> } | null;
            if (!acc || !Array.isArray(acc.out)) continue;
            let cnt = 0;
            for (const slot of acc.out) cnt += Number(slot?.cnt || 0);
            byDay[r2.dated] = (byDay[r2.dated] || 0) + cnt;
          }
          // Map our 7 day labels (MM-DD) back to YYYY-MM-DD and
          // fill the chart's data array. Days with no records
          // stay at 0 (so a 0-token day is visible as a dip).
          const data = days.map((mmdd) => {
            const ymd = today.getFullYear() + '-' + mmdd;
            return byDay[ymd] || 0;
          });
          // Mutate the existing chart's dataset in place so the
          // Canvas animation re-renders smoothly without a full
          // destroy/recreate cycle.
          const ch = (inst as unknown as {
            data: { datasets: Array<{ data: number[] }> };
            update: (mode?: string) => void;
          });
          ch.data.datasets[0].data = data;
          ch.update('none');
        } catch {
          // Network/JSON failure: keep the seeded zeros.
        }
      })();
    }
  }

  // Kick off IP geolocation (async) -- Overview owns this.
  // Phase 12.15: the lookup now goes through /api/ipgeo (server-side
  // cache + ip2location.io fetch). The previous browser-to-ip2location
  // path was silently violating CSP connect-src 'self' and burned the
  // free-tier rate limit on every Overview open. The config object is
  // reconstructed the same way renderTabLogs does it (read from
  // window.API_BASE + window.timestop.state.csrfToken) so we don't have
  // to thread it through renderTabOverview's signature.
  const resolveIp = win().resolveAndShowIp;
  if (typeof resolveIp === 'function') {
    const wcfg = win() as {
      API_BASE?: string;
      timestop?: { state?: { csrfToken?: string | null } };
    };
    const cfg = {
      apiBase: wcfg.API_BASE ?? '/api',
      csrfToken: (wcfg.timestop && wcfg.timestop.state && wcfg.timestop.state.csrfToken) || null,
    };
    (resolveIp as (s: TimestopState, c: typeof cfg) => Promise<void>)(state, cfg);
  }
}

// renderTabLogs -- fetch the per-terminal log list via /api/logs?mid=N
// and then delegate to renderTerminalLogs. Phase 12.11: per-terminal
// logs no longer ride on the /api/terminal response, so the Logs tab
// pulls them on demand the first time the user activates it. Subsequent
// activations reuse the cached `_logs` array on the terminal object
// until the 5s log polling tick refreshes it. The fetch is awaited so
// the user sees the rows as soon as they arrive; while in flight the
// tab shows a "Loading logs..." placeholder.
//
// /api/logs is role/group scoped server-side: if an operator somehow
// reaches the Logs tab for a terminal outside their op_id, the
// endpoint returns 403 and we render an empty list.
export async function renderTabLogs(t: Terminal, state: TimestopState): Promise<void> {
  const container = document.getElementById('modal-tab-1');
  if (container) {
    // Lightweight "loading" placeholder so the user gets immediate
    // feedback that the tab activated and is fetching. Cleared by
    // renderTerminalLogs() once the rows land.
    container.innerHTML = '<div class="text-gray-400 py-4">' + i18n('tab_logs.loading_logs') + '</div>';
  }
  // Build the apiBase + csrfToken config the same way the inline
  // scripts do (see index.php's startLogPolling). window.API_BASE
  // is set by the inline boot in index.php; the bundle's
  // navigation.ts and terminal-modal.ts use the same fallback.
  const w2 = win() as {
    API_BASE?: string;
    timestop?: { state?: { csrfToken?: string | null } };
    fetchLogs?: (s: TimestopState, c: { apiBase: string; csrfToken: string | null }, opts: { mid: number | string }) => Promise<unknown[]>;
  };
  const config = {
    apiBase: (w2.API_BASE as string | undefined) ?? '/api',
    csrfToken: (w2.timestop && w2.timestop.state && w2.timestop.state.csrfToken) || null,
  };
  if (typeof w2.fetchLogs === 'function') {
    await w2.fetchLogs(state, config, { mid: t.id });
  }
  const rtl = win().renderTerminalLogs;
  if (typeof rtl === 'function') (rtl as (t: Terminal | null, full: boolean) => void)(t, true);
}

// Admin-only "Move terminal" block. Moved here from the Commands tab
// (which is no longer rendered): the operator-facing command buttons
// went away, but reassigning a terminal across operator groups is still
// needed from the Settings tab. Hidden for non-admins.
export function moveSectionHTML(t: Terminal, state: TimestopState): string {
  if (state.currentUser?.role !== 'admin') return '';

  const currentOid = t.oid != null ? Number(t.oid) : 0;
  // Sorted group list for the <select>. "Unassigned" is the sentinel
  // option with value="0" (matches the wire protocol: oid=0 -> NULL).
  const groupOptions: string[] = (state.groups || [])
    .map(g => ({ id: Number(g.id), name: String(g.name || ('Group ' + g.id)) }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(g => `<option value="${g.id}"${g.id === currentOid ? ' selected' : ''}>${escapeHtml(g.name)}</option>`);

  const currentName = currentOid === 0
    ? '<em>' + i18n('common.unassigned') + '</em>'
    : escapeHtml(((state.groups || []).find(g => Number(g.id) === currentOid) || {}).name || ('Group ' + currentOid));

  return `
    <div class="border-t p-3 pt-6 mt-2">
      <h4 class="font-semibold mb-2">${i18n('terminal_modal.move_section_title')}</h4>
      <p class="text-xs text-gray-500 mb-3">${i18n('terminal_modal.move_section_help')}</p>
      <div class="flex flex-col sm:flex-row gap-2">
        <select id="move-target" class="flex-1 bg-gray-100 rounded-2xl px-4 py-3 text-sm">
          <option value="0"${currentOid === 0 ? ' selected' : ''}>(${i18n('common.unassigned')})</option>
          ${groupOptions.join('')}
        </select>
        <button id="move-go" type="button" class="px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-medium">${i18n('terminal_modal.move')}</button>
      </div>
      <div id="move-current" class="text-xs text-gray-400 mt-2">${i18n('tab_commands.currently', { name: currentName })}</div>
    </div>`;
}

// Wire the Move button rendered by moveSectionHTML(). Clicks are
// delegated to window.moveTerminal so the showConfirm / spinner pattern
// stays in the bundle layer; main.ts installs the HTML onclick adapter
// that injects state + config.
export function wireMoveSection(state: TimestopState): void {
  const moveBtn = document.getElementById('move-go');
  if (!moveBtn) return;
  moveBtn.addEventListener('click', () => {
    const select = document.getElementById('move-target') as HTMLSelectElement | null;
    if (!select) return;
    const target = parseInt(select.value, 10);
    if (!isFinite(target) || target < 0) return;
    const moveFn = win().moveTerminal as ((s: TimestopState, cfg: ApiFetchConfig, fn: ApiFetchFn, oid: number) => void) | undefined;
    const config = (win().__timestop_config as ApiFetchConfig | undefined) || {
      apiBase: '/api/', csrfToken: (state as unknown as { csrfToken: string | null }).csrfToken,
    };
    const apiFn = (win().apiFetchCore as ApiFetchFn | undefined) || apiFetch;
    if (typeof moveFn === 'function') moveFn(state, config, apiFn, target);
  });
}

// renderTabSettings -- paints the Settings tab (frozen, force_sync,
// buy_mode toggles, bet prices/tokens per bet). This is the only
// destructive re-paint: in-progress user edits in the price inputs
// are wiped on every call.
export function renderTabSettings(t: Terminal, state: TimestopState): void {
  const recv = t.set_recv || {};
  const send = t.set_send || {};
  const currencies = Object.values(t.cur_recv || {});

  // Timing controls: timer_value (win window, 3..10 s, step 1) and
  // demo_time (attract-mode countdown, 0 = off, 10..360 s in 10 s
  // steps). Pending edits win over the last value the cabinet sent.
  const pendingCfg = (t.set_save || {}) as Record<string, unknown>;
  const timerShown = Number(pendingCfg.timer_value ?? (recv as Record<string, unknown>).timer_value ?? 3);
  const demoShown = Number(pendingCfg.demo_time ?? (recv as Record<string, unknown>).demo_time ?? 0);
  const modeValue = Number((recv as Record<string, unknown>).mode ?? 0);
  const modeChip = modeValue
    ? '<span class="leading-none">🪙</span><span class="ml-1">' + escapeHtml(i18n('common.cash')) + '</span>'
    : '<span class="leading-none">🎮</span><span class="ml-1">' + escapeHtml(i18n('common.fun')) + '</span>';

  const demoOptions = Array.from({ length: 37 }, (_, i) => i * 10)
    .map(v => `<option value="${v}"${v === demoShown ? ' selected' : ''}>${v === 0 ? i18n('tab_settings.off') : v + ' s'}</option>`)
    .join('');

  // Icons MUST stay in sync with `cardStatusIcons` in render.ts
  // (the strip on the terminal list cards). The Settings tab
  // reuses the exact same glyphs so a button reads the same as
  // the equivalent ON/OFF state on the dashboard. Pass an empty
  // string for buttons with no icon -- the placeholder span
  // below keeps the label baseline aligned across all buttons
  // in the same row.
  const statusButtonHTML = (key: string, value: number, label: string, padding?: string, icon: string = ''): string => {
    const isRecv = Number((recv as Record<string, unknown>)[key]) === Number(value);
    const isSend = hasOwn(send as Record<string, unknown>, key) && Number((send as Record<string, unknown>)[key]) === Number(value);
    const pad = padding || 'px-4 py-2';
    let cls: string;
    if (isSend && !isRecv) {
      cls = 'pending unsaved';
    } else if (isRecv) {
      cls = 'active';
    } else {
      cls = 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400';
    }
    // icon row is ALWAYS rendered so the label sits at the same
    // vertical baseline across all buttons regardless of whether
    // this particular button has a glyph. text-lg + leading-none
    // keeps the row a fixed height even when the icon is empty.
    const iconRow = icon
      ? `<span class="block text-lg leading-none mb-0.5">${icon}</span>`
      : `<span class="block text-lg leading-none mb-0.5">&nbsp;</span>`;
    return `<button type="button" data-key="${key}" data-value="${value}" onclick="setPendingSetting('${key}', ${value})" class="${pad} rounded-xl text-sm font-medium transition-colors ${cls}"><span class="inline-flex flex-col items-center leading-none">${iconRow}${label}</span></button>`;
  };

  const isOp = isOperator(state);
  const showAdminCtrls = !isOp;
  const statusCols = isOp ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2 md:grid-cols-2';

  // The Operation (Testing / Normal) row was dropped from this tab on
  // request. The `runtime` column and its admin-only endpoint
  // (/api/terminal_set_runtime) still exist — the modal/card keep
  // their dashed-orange "testing" border — but nothing here toggles it.

  const tab2 = document.getElementById('modal-tab-2');
  if (tab2) {
    tab2.innerHTML = `
      <div class="space-y-8 max-w-3xl">
        <div>
          <h4 class="font-semibold mb-4">${i18n('tab_settings.terminal_status')}</h4>
          <div class="grid ${statusCols} gap-4">
            ${showAdminCtrls ? `
            <div class="p-4 rounded-2xl border-2 border-transparent bg-gray-100">
              <div class="mb-2 inline-flex">${i18n('tab_settings.frozen')}</div>
              <div class="flex gap-2 inline-flex float-right">
                ${statusButtonHTML('frozen', 0, i18n('common.btn_off'))}
                ${statusButtonHTML('frozen', 1, i18n('common.btn_on'), undefined, '❄️')}
              </div>
            </div>
            <div class="p-4 rounded-2xl border-2 border-transparent bg-gray-100">
              <div class="mb-2 inline-flex">${i18n('tab_settings.force_sync')}</div>
              <div class="flex gap-2 inline-flex float-right">
                ${statusButtonHTML('force_sync', 0, i18n('common.btn_off'), undefined, '❌')}
                ${statusButtonHTML('force_sync', 1, i18n('common.btn_on'))}
              </div>
            </div>
            ` : ''}
          </div><br>
          <div class="grid grid-cols-1 gap-4">
            <div class="p-4 rounded-2xl border-2 border-transparent bg-gray-100">
              <div class="mb-2 inline-flex">${i18n('tab_settings.mode')}</div>
              <!-- Read-only: the cabinet owns the game mode; the BO only
                   reports what it last sent. Nothing here is queued. -->
              <div class="inline-flex float-right items-center gap-2 px-3 py-2 text-sm rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed select-none"
                   title="${i18n('tab_settings.mode_readonly')}">${modeChip}</div>
            </div>
          </div>
        </div>

        <div>
          <h4 class="font-semibold mb-4">${i18n('tab_settings.timing')}</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="p-4 rounded-2xl bg-gray-100">
              <div class="text-xs text-gray-500 mb-2">${i18n('tab_settings.timer_value')}</div>
              <div class="flex items-center gap-2">
                <input type="number" min="3" max="10" step="1" value="${timerShown}"
                       onchange="setPendingSetting('timer_value', Number(this.value))"
                       class="w-24 bg-white border rounded-xl px-3 py-2 text-sm">
                <span class="text-sm text-gray-500">${i18n('tab_settings.seconds')}</span>
              </div>
              <div class="text-[11px] text-gray-400 mt-1">${i18n('tab_settings.timer_value_help')}</div>
            </div>
            <div class="p-4 rounded-2xl bg-gray-100">
              <div class="text-xs text-gray-500 mb-2">${i18n('tab_settings.demo_time')}</div>
              <div class="flex items-center gap-2">
                <select onchange="setPendingSetting('demo_time', Number(this.value))"
                        class="w-32 bg-white border rounded-xl px-3 py-2 text-sm">
                  ${demoOptions}
                </select>
              </div>
              <div class="text-[11px] text-gray-400 mt-1">${i18n('tab_settings.demo_time_help')}</div>
            </div>
          </div>
        </div>

        <div>
          <h4 class="font-semibold mb-4">${i18n('tab_settings.bets')}</h4>
          ${[1,2,3].map(bet => {
            // Real cabinet shape: flat scalars in settings.prices —
            // p1..p3 = price per bet (minor units), t1..t3 = tokens per bet.
            const rPrices = ((recv as Record<string, unknown>).prices || {}) as Record<string, number>;
            const sPrices = (t.set_save && (t.set_save as Record<string, unknown>).prices
              ? (t.set_save as Record<string, unknown>).prices
              : {}) as Record<string, number>;
            const cur0 = currencies[0] as Record<string, unknown> | undefined;
            const div = Number(cur0?.div ?? 1) || 1;
            const iso0 = (cur0?.ISO as string | undefined) || '';
            const step = cur0?.step ? Number(cur0.step) / div : 0.1;
            const pRecv = Number(rPrices['p' + bet] ?? 0);
            const pSend = sPrices['p' + bet];
            const pDisplay = pSend !== undefined ? Number(pSend) / div : pRecv / div;
            const tRecv = Number(rPrices['t' + bet] ?? 0);
            const tSend = sPrices['t' + bet];
            const tDisplay = tSend !== undefined ? Number(tSend) : tRecv;
            return `
            <div class="p-4 rounded-2xl mb-4 bg-gray-100">
              <div class="font-medium mb-3">${i18n('tab_settings.bet_n', { n: bet })}</div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div class="text-xs text-gray-500 mb-1">${i18n('tab_settings.price')}${iso0 ? ' (' + iso0 + ')' : ''}</div>
                  <input type="number" step="${step}" value="${pDisplay}" disabled
                    class="w-full bg-gray-50 text-gray-500 border rounded-xl px-3 py-2 text-sm cursor-not-allowed">
                </div>
                <div>
                  <div class="text-xs text-gray-500 mb-1">${i18n('tab_settings.tokens')}</div>
                  <input type="number" step="1" min="0" value="${tDisplay}" disabled
                    class="w-full bg-gray-50 text-gray-500 border rounded-xl px-3 py-2 text-sm cursor-not-allowed">
                </div>
              </div>
            </div>`;
          }).join('')}
        </div>
        <button id="save-settings-btn" type="button" onclick="saveSettings()"
                class="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-3xl font-medium mt-6">${i18n('common.save_all')}</button>
        ${moveSectionHTML(t, state)}
        <!-- Reboot: the only command left, moved here from the old
             Commands tab. No section heading -- the button carries it. -->
        <div class="border-t p-3 pt-6 mt-2">
          <div class="flex flex-wrap gap-3 justify-center">
            <button type="button" data-cmd="reboot" data-val="1"
                    class="cmd-btn px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-medium">${i18n('tab_commands.reboot')}</button>
          </div>
        </div>
      </div>`;
    // The Move block used to be wired by renderTabCommands; it lives
    // here now (the Commands tab is gone).
    wireMoveSection(state);
    // Same for the Reboot button that closes the tab.
    wireCmdButtons(state);
  }
}

// renderTabCommands -- paints the Commands tab (hopper levels display,
// Payout 1/2/3 + Reboot buttons). The cmdBtn helper builds a button
// with the right visual state based on t.cmd_send.command.{cmd}.
//   - 'pending unsaved' classes: command already queued, waiting for the
//     terminal to fetch it
//   - plain: not queued
// On click the button gets `unsaved` added so the user sees the
// "pending confirmation" state until they Yes/No the confirm modal.
export function renderTabCommands(t: Terminal, state: TimestopState): void {
  let bnrData = { balances: [0, 0, 0], refill: [0, 0, 0] };
  if (t.bnr_recv) {
    try {
      const raw = typeof t.bnr_recv === 'string' ? JSON.parse(t.bnr_recv as string) : t.bnr_recv;
      if (raw && Array.isArray(raw.balances)) bnrData.balances = raw.balances;
      if (raw && Array.isArray(raw.refill)) bnrData.refill = raw.refill;
    } catch { /* keep zeros */ }
  }

  const currencies = Object.values(t.cur_recv || {});

  const sendCommand = (t.cmd_send as Record<string, unknown> | null | undefined)?.command as Record<string, unknown> | undefined;
  const cmdBtn = (cmd: string, val: string, label: string, baseClass: string): string => {
    const isQueued = !!(sendCommand && String(sendCommand[cmd] ?? '') === val);
    const pendingCls = isQueued ? ' pending unsaved' : '';
    return `<button id="${cmd}_${val}" type="button" data-cmd="${cmd}" data-val="${val}" class="cmd-btn ${baseClass} rounded-2xl${pendingCls}">${label}</button>`;
  };

  const tab3 = document.getElementById('modal-tab-3');
  if (tab3) {
    // The admin "Move terminal" block was moved to the Settings tab
    // (moveSectionHTML) when the Commands tab was hidden.
    tab3.innerHTML = `
      <div class="space-y-6">
        <div class="grid grid-cols-1 gap-4">
          <div class="bg-gray-100 rounded-2xl p-2 text-center">
            <div class="text-xs text-gray-500">${i18n('tab_overview.hopper_levels')}</div><hr>
            <div class="grid grid-cols-3 gap-2 font-medium truncate text-md mt-1">
              ${bnrData.refill.map((val, idx) => `<div><div class="text-xs text-gray-500">#${idx+1}</div><div id="modal-hoplvl${idx}" class="">${val}</div></div>`).join('')}
            </div>
          </div>
          <div class="bg-gray-100 rounded-2xl p-2 text-center">
            <div class="text-xs text-gray-500">${i18n('tab_overview.balances')}</div><hr>
            <div class="grid grid-cols-3 gap-2 font-medium truncate text-md mt-1">
              ${currencies.map((cur, idx) => {
                const iso = cur.ISO || `C${idx}`;
                const displayedValue = formatCurrencyByISO(t, bnrData.balances[idx], iso);
                return `<div><div class="text-xs text-gray-500">${iso}</div><div id="modal-bal${idx}" class="">${displayedValue}</div></div>`;
              }).join('')}
            </div>
          </div>
        </div>
        <div id="cmd-refresh-status" class="text-xs text-gray-400 text-center hidden">
          <span class="inline-block w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
          ${i18n('tab_commands.auto_refresh_status')}
        </div>
        <div class="border-t p-3 pt-6">
          <h4 class="font-semibold mb-4">${i18n('tab_commands.commands')}</h4>
          <div class="flex flex-wrap gap-3 justify-center">
            ${cmdBtn('payout', '1', i18n('tab_commands.payout_n', { n: 1 }), 'px-5 py-3 bg-red-500  text-white')}
            ${cmdBtn('payout', '2', i18n('tab_commands.payout_n', { n: 2 }), 'px-5 py-3 bg-red-500  text-white')}
            ${cmdBtn('payout', '3', i18n('tab_commands.payout_n', { n: 3 }), 'px-5 py-3 bg-red-500  text-white')}
          </div>
          <div class="flex flex-wrap py-8 gap-3 justify-center">
            ${cmdBtn('reboot', '1', i18n('tab_commands.reboot'),   'px-5 py-3 bg-amber-500 text-white')}
          </div>
        </div>
      </div>`;
  }

  wireCmdButtons(state);
}

// Wire every .cmd-btn on the page (Reboot used to live only in the
// Commands tab; it now sits at the end of the Settings tab, so the
// wiring has to run from whichever tab painted the button).
export function wireCmdButtons(state: TimestopState): void {
  document.querySelectorAll('.cmd-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const cmd = btn.getAttribute('data-cmd') || '';
      const val = btn.getAttribute('data-val') || '1';

      // Mark the clicked button as `unsaved` immediately (orange
      // highlight) so the user sees the "pending confirmation" state.
      // The class is removed when the user dismisses (Yes/No) the
      // confirm modal -- the modal's yes/no handlers reset all
      // .unsaved classes on .cmd-btn.
      // Drop `pending` if it was there (we're transitioning from
      // "already queued" to "just clicked, not confirmed").
      document.querySelectorAll('.cmd-btn').forEach(sss => {
        sss.classList.remove('pending');
        sss.classList.remove('unsaved');
      });

      btn.classList.add('unsaved');

      const scFn = win().showConfirm;
      if (typeof scFn === 'function') {
        const config = win().__timestop_config as ApiFetchConfig | undefined;
        const apiFn = win().apiFetchCore as ApiFetchFn | undefined;
        if (config && apiFn) (scFn as (s: TimestopState, c: string, v: string, cfg: ApiFetchConfig, fn: ApiFetchFn) => void)(state, cmd, val, config, apiFn);
      }
    });
  });

  // The Move button wiring lives in wireMoveSection(), called by
  // renderTabSettings -- the Commands tab no longer hosts that block.
}

// moveTerminal -- admin-only. Reads the chosen oid from the Move
// select, asks for confirmation via showConfirm, then POSTs to
// /api/terminal_move. On success updates state.currentTerminal.oid
// (and the terminals[] map) so subsequent renders show the new
// group without a full refetch.
//
// Re-fetches /api/terminal on success so reset_list and any other
// server-derived fields stay consistent (the move itself only
// touches mach.oid / mach.op_id, but a fresh read also picks up
// any audit fields or future columns).
export async function moveTerminal(
  state: TimestopState,
  config: ApiFetchConfig,
  apiFetchFn: ApiFetchFn,
  targetOid: number,
): Promise<void> {
  const t = state.currentTerminal;
  if (!t) return;
  // Resolve target name for the confirm message; "(Unassigned)" for 0.
  const targetName = targetOid === 0
    ? i18n('common.unassigned')
    : ((state.groups || []).find(g => Number(g.id) === targetOid) || {}).name || ('Group ' + targetOid);
  const currentOid = t.oid != null ? Number(t.oid) : 0;
  const currentName = currentOid === 0
    ? i18n('common.unassigned')
    : ((state.groups || []).find(g => Number(g.id) === currentOid) || {}).name || ('Group ' + currentOid);

  showConfirm(state, {
    title: i18n('terminal_modal.move_confirm_title'),
    message: i18n('terminal_modal.move_confirm_message', { terminal: (t.name || ('terminal ' + t.mid)), from: currentName, to: targetName }) + '\n\n',
    onYes: async () => {
      try {
        showSpinner(i18n('terminal_modal.move_in_progress'));
        const r = await apiFetchFn('terminal_move',
          { method: 'POST', body: { mid: t.mid, oid: targetOid } },
          config);
        if (!r.ok || !r.data || (r.data as Record<string, unknown>).error) {
          const errKey = String((r.data as Record<string, unknown> | null)?.error ?? '') || 'move_failed';
          console.warn('move failed:', errKey, r);
          // Surface as a simple alert -- the user is in the modal,
          // and the inline confirm flow doesn't have a dedicated
          // error region. Future: route through the spinner message
          // channel instead.
          alert(i18n('terminal_modal.move_failed', { code: errKey }));
          return;
        }
        // Update local state so the modal reflects the new group
        // immediately. We also refresh the global terminals[] map so
        // closing the modal and re-opening (or a re-render) shows
        // the right group in the list view.
        // The Terminal type's `oid` doesn't allow null, so unassign
        // becomes 0 -- renderTabCommands treats 0 as Unassigned.
        t.oid = targetOid > 0 ? targetOid : 0;
        if (state.terminals && state.terminals[String(t.id)]) {
          state.terminals[String(t.id)].oid = t.oid;
        }

        // Re-fetch the terminal in the background so the rest of the
        // modal (reset_list, acc_*, etc.) stays consistent. Render
        // the Commands tab right away so the "Currently:" line
        // updates to the new group without a full modal close.
        try {
          const refetch = await apiFetchFn('terminal?id=' + encodeURIComponent(String(t.id)), { method: 'GET' }, config);
          if (refetch.ok && refetch.data) {
            const fresh = refetch.data as Terminal;
            // Shallow-merge: don't blow away the in-place t object that
            // other code may still be holding pointers to.
            Object.assign(t, fresh);
            // Re-render the Commands tab to refresh the "Currently:"
            // label and the select's selected option.
            renderTabCommands(t, state);
          }
        } catch (e) {
          console.warn('post-move refetch failed (continuing):', (e as Error).message);
        }
      } catch (e) {
        console.warn('move exception:', (e as Error).message);
        alert(i18n('terminal_modal.move_failed', { code: (e as Error).message }));
      } finally {
        hideSpinner();
      }
    },
  });
}

// renderTabAccounting -- paints the Accounting tab shell (period toggle
// + body container) and runs the initial render for acc_shor. The
// period toggle buttons are wired here too so a re-paint re-binds
// them (a small leak risk if the function is called repeatedly --
// listeners are added on every call; acceptable since the old nodes
// are GC'd when their parent innerHTML is replaced).
export function renderTabAccounting(t: Terminal, state: TimestopState): void {
  const tab4 = document.getElementById('modal-tab-4');
  if (tab4) {
    tab4.innerHTML = `
      <div class="space-y-6">
        <div class="flex justify-center">
          <div id="accounting-period-toggle" class="bg-gray-100 rounded-2xl p-1 flex">
            <button data-period="acc_shor" class="px-4 py-2 rounded-xl text-sm">${i18n('tab_accounting.period_short')}</button>
            <button data-period="acc_long" class="px-4 py-2 rounded-xl text-sm">${i18n('tab_accounting.period_long')}</button>
            <button data-period="acc_range" class="px-4 py-2 rounded-xl text-sm">${i18n('tab_accounting.period_ranged')}</button>
          </div>
        </div>
        <div id="accounting-body" class="bg-gray-200 rounded-2xl sm:p-6 py-6"></div>
      </div>`;
  }

  // Wire accounting period toggle
  const body = document.getElementById('accounting-body');
  if (body) {
    const config = (win().__timestop_config as ApiFetchConfig | undefined) || {
      apiBase: '/api/', csrfToken: (state as unknown as { csrfToken: string | null }).csrfToken,
    };
    const renderFor = (periodKey: string): void => {
      if (periodKey === 'acc_range') {
        const rFn = win().renderAccountingRanged;
        if (typeof rFn === 'function') {
          // Ranged is async (does its own fetch on demand).
          void (rFn as (s: TimestopState, c: HTMLElement, cfg: ApiFetchConfig) => Promise<void>)(state, body, config);
        }
      } else {
        const raFn = win().renderAccounting;
        if (typeof raFn === 'function') {
          (raFn as (s: TimestopState, c: HTMLElement, p: string) => void)(state, body, periodKey);
        }
      }
      document.querySelectorAll('#accounting-period-toggle button').forEach(b => {
        const active = b.getAttribute('data-period') === periodKey;
        b.classList.toggle('bg-white', active);
        b.classList.toggle('shadow-sm', active);
        b.classList.toggle('font-medium', active);
        b.classList.toggle('text-gray-500', !active);
        b.classList.toggle('hover:text-gray-700', !active);
      });
    };
    document.querySelectorAll('#accounting-period-toggle button').forEach(btn => {
      btn.addEventListener('click', () => renderFor(btn.getAttribute('data-period') || 'acc_shor'));
    });
    renderFor('acc_shor');
  }
}
